import type { RuntimeConfig } from '@nuxt/schema';
import { useAuthStore } from '~/stores/auth';
import type { APIErrorResponse, FetchParams } from '~/interfaces/api/main';
import type { ApiResponse } from '~/types/main';
import { getArcSheetHeaders, getAuthHeaders, normalizeApiError, normalizeJsonApiResponse } from '~/composables/http/utils';
import { isJsonApiResponse } from '~/composables/useUtils';
import { useToastService } from '~/composables/useToastService';

// Écritures (POST/PUT/PATCH/DELETE) : `$fetch`, pas `useFetch`.
//
// `useAuthFetch` et `useAuthFetchNext` enveloppent tous deux `useFetch`, c'est-à-dire
// `useAsyncData` : une primitive de LECTURE déclarative, avec clé de cache, données partagées et
// watcher de rafraîchissement. Nuxt la réserve au setup, aux plugins et aux middlewares, et prévoit
// `$fetch` pour les appels déclenchés par une interaction — l'avertissement est même dans son code
// (« Component is already mounted, please use $fetch instead »).
//
// Appelée depuis un gestionnaire de clic, elle laisse derrière elle un watcher jamais désabonné et
// une entrée de cache jamais libérée : `asyncData.js` ne les nettoie que via `onScopeDispose`, qui
// n'a aucun effet hors d'un scope réactif. Deux écritures identiques partagent en plus la même clé,
// donc la même entrée. Rien de tout cela n'a de sens pour une écriture, et une écriture rejouée
// crée un doublon — un POST rejoué crée une seconde entité.
//
// Le contrat de retour est CELUI de `useAuthFetchNext` (`ApiResponse` normalisé, toast d'erreur sauf
// `silentError`) : migrer un appel, c'est changer le nom de la fonction, pas son appelant.
export async function authMutate (url: string, params?: FetchParams): Promise<ApiResponse> {
  const authStore = useAuthStore();
  const config: RuntimeConfig = useRuntimeConfig();

  let transactionId: string | null = null;
  let statusCode: number | null = null;

  const { silentError, ...fetchParams } = params ?? {};
  const headers: Record<string, string> = {
    ...getArcSheetHeaders(),
    ...(params?.headers || {}),
    ...(await getAuthHeaders()),
  };

  try {
    // `headers` et `onResponse` sont posés APRÈS le spread : l'appelant ne peut pas remplacer
    // l'authentification ni la lecture de l'en-tête de transaction.
    const data = await $fetch(url, {
      baseURL: config.public.API_BASE_URL,
      ...fetchParams,
      headers,
      onResponse ({ response }) {
        transactionId = response.headers.get('X-Transaction-Id') || null;
        statusCode = response.status;
      },
    });

    if (statusCode === 204) {
      return { transactionId, errors: [], statusCode: 204 };
    }

    if (isJsonApiResponse(data)) {
      return normalizeJsonApiResponse(data);
    }

    return {
      transactionId,
      errors: [{
        title: 'Format de données non reconnu',
        detail: 'Les données reçues ne sont pas au format attendu.',
      }],
      statusCode: statusCode ?? undefined,
    } as APIErrorResponse;
  } catch (error) {
    // `$fetch` lève sur tout statut non-2xx : la charge d'erreur est sur l'exception, là où
    // `useFetch` la posait dans une ref.
    const failure = error as { status?: number; statusCode?: number; data?: unknown; message?: string };
    const status = failure.status ?? failure.statusCode ?? 500;

    // Même geste que le wrapper de lecture : un token périmé est renouvelé pour les appels
    // suivants. L'appel courant n'est PAS rejoué — sur une écriture, un rejeu automatique est
    // exactement ce qu'on cherche à éviter.
    if (status === 401) {
      await authStore.fetchToken();
    }

    if (!silentError) {
      const problem = (failure.data && typeof failure.data === 'object')
        ? failure.data as { title?: string }
        : {};
      const [shown] = normalizeApiError(failure.data, status, transactionId, failure.message).errors;
      useToastService().add({
        severity: 'error',
        summary: problem.title || 'Goldorac API Request Failed',
        detail: shown?.detail || 'Unknown error',
        life: 3000,
      });
    }

    return normalizeApiError(failure.data, status, transactionId);
  }
}
